import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Table from "../../../components/Table/Table";
import {
  getRegions,
  postRegion,
  patchRegion,
  deleteRegion,
} from "../../../services/olympiad/regionService";
import RegionPageHeader from "./PageHeader";
import RegionFilters from "./Filters";
import RegionForm from "./RegionForm";
import { useRegionColumns } from "./columns";
import { toast } from "react-toastify";
import { Button } from "../../../components/forms/Buttons/Button";

export default function Regions() {
  const [t] = useTranslation();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [pageCount, setPageCount] = useState(1);

  // Filters
  const [isFullFilter, setIsFullFilter] = useState(null);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: "",
    team_limit: 80,
  });

  const fetchData = () => {
    setLoading(true);
    getRegions({ is_full: isFullFilter, page: page, page_size: 50 })
      .then((res) => {
        setData(res.results || []);
        setCount(res.count || 0);
        setPageCount(Math.ceil((res.count || 0) / 50));
      })
      .catch((err) => {
        console.error("Error fetching regions:", err);
        toast.error(t("admin.error_fetching_data"));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [page, isFullFilter]);

  const handleCreate = () => {
    postRegion(formData)
      .then(() => {
        toast.success(t("admin.region_created"));
        setIsCreateModalOpen(false);
        setFormData({ name: "", team_limit: 80 });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_creating_region")
        );
      });
  };

  const handleEdit = (region) => {
    setEditingRegion(region);
    setFormData({
      name: region.name || "",
      team_limit: region.team_limit || "",
    });
    setIsEditModalOpen(true);
  };

  const handleUpdate = () => {
    patchRegion(editingRegion.uuid, formData)
      .then(() => {
        toast.success(t("admin.region_updated"));
        setIsEditModalOpen(false);
        setEditingRegion(null);
        setFormData({ name: "", team_limit: 80 });
        fetchData();
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.detail || t("admin.error_updating_region")
        );
      });
  };

  const handleDelete = (region) => {
    if (toast.isActive(region.uuid)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>{t("admin.confirm_delete_region", { region: region.name })}</p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.delete")}
            color="error"
            onClick={() => {
              toast.dismiss(region.uuid);
              deleteRegion(region.uuid)
                .then(() => {
                  toast.success(t("admin.region_deleted"));
                  fetchData();
                })
                .catch((err) => {
                  toast.error(
                    err.response?.data?.detail ||
                      t("admin.error_deleting_region")
                  );
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(region.uuid)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: region.uuid }
    );
  };

  const columns = useRegionColumns(handleEdit, handleDelete);

  return (
    <div className="p-6">
      <RegionPageHeader onCreateClick={() => setIsCreateModalOpen(true)} />

      <RegionFilters
        isFullFilter={isFullFilter}
        setIsFullFilter={setIsFullFilter}
      />

      <Table
        columns={columns}
        data={data}
        loading={loading}
        count={count}
        pageNumber={page}
        pageCount={pageCount}
        nextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
        previousPage={() => setPage((p) => Math.max(p - 1, 1))}
        gotoPage={(p) => setPage(p)}
      />

      <RegionForm
        isOpen={isCreateModalOpen}
        isEdit={false}
        formData={formData}
        setFormData={setFormData}
        onClose={() => {
          setIsCreateModalOpen(false);
          setFormData({ name: "", team_limit: 80 });
        }}
        onSubmit={handleCreate}
      />

      <RegionForm
        isOpen={isEditModalOpen}
        isEdit={true}
        formData={formData}
        setFormData={setFormData}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingRegion(null);
          setFormData({ name: "", team_limit: 80 });
        }}
        onSubmit={handleUpdate}
      />
    </div>
  );
}
